import { Component } from '@theme/component';
import { getBundleStore } from '@theme/pdp-bundle-store';

/**
 * @typedef {Object} Plan
 * @property {number} id - Subscription variant id
 * @property {string} term - `yearly`, `monthly` or a handleized title
 * @property {string} trialPeriod - Trial period in seconds
 * @property {string} sellingPlanId
 * @property {string} label
 * @property {string} recap - Buy card recap label (ex. "Withings+ yearly, 1 month free")
 */

/**
 * Buy Box V2 Withings+ plans. The checked radio joins the add-to-cart batch through the PDP bundle
 * store (contributor `subscription`), with the same line contract as `subscription-block.js`:
 * selling plan, `_subscription_term`, `_trial_period`, nested under the device via `parent_id`
 * and linked by `_sales_line_item_group_id`.
 *
 * @extends {Component}
 */
class BuyBoxV2Plans extends Component {
  /** @type {(() => void) | null} */
  #unregister = null;

  connectedCallback() {
    super.connectedCallback();
    this.#register();
    document.addEventListener('model:update', this.#onModelUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unregister?.();
    this.#unregister = null;
    document.removeEventListener('model:update', this.#onModelUpdate);
  }

  /** Publishes the newly checked plan to the bundle store. */
  selectPlan() {
    getBundleStore().publish('subscription');
  }

  /** @returns {Plan | null} */
  get #plan() {
    const input = this.querySelector('input[type="radio"]:checked');
    if (!(input instanceof HTMLInputElement) || input.value === 'none') return null;

    return {
      id: Number(input.value),
      term: input.dataset.planTerm ?? '',
      trialPeriod: input.dataset.planTrialPeriod ?? '',
      sellingPlanId: input.dataset.planSellingPlanId ?? '',
      label: input.dataset.planLabel ?? '',
      recap: input.dataset.planRecap ?? '',
    };
  }

  #register() {
    const store = getBundleStore();
    store.setContext({ productId: this.dataset.productId ?? '', sectionId: this.dataset.sectionId ?? '' });
    this.#unregister = store.register({
      name: 'subscription',
      getSectionId: () => this.dataset.sectionId ?? '',
      order: 5,
      getItems: () => {
        const plan = this.#plan;
        if (!plan) return [];

        /** @type {Record<string, string>} */
        const properties = { _bundle_role: 'subscription', _subscription_term: plan.term };
        if (plan.trialPeriod) properties._trial_period = plan.trialPeriod;
        /** @type {{ id: number, quantity: number, properties: Record<string, string>, selling_plan?: number }} */
        const item = { id: plan.id, quantity: 1, properties };
        if (plan.sellingPlanId) item.selling_plan = Number(plan.sellingPlanId);
        return [item];
      },
      getDisplay: () => {
        const plan = this.#plan;
        if (!plan) return [];

        return [
          {
            role: 'subscription',
            title: this.dataset.subTitle || plan.label,
            subtitle: plan.recap || plan.label,
            priceCents: 0,
            priceHtml: '',
            variantId: String(plan.id),
          },
        ];
      },
      transformPayload: (items, ctx) => {
        if (!this.#plan) return items;

        const mainVariantId = Number(ctx.mainVariantId);
        for (const item of items) {
          const role = item.properties?._bundle_role;
          if (role === 'main' || role === 'subscription') {
            item.properties = { ...(item.properties ?? {}), _sales_line_item_group_id: ctx.bundleKey };
          }
          if (role === 'subscription' && mainVariantId) item.parent_id = mainVariantId;
        }
        return items;
      },
      onCommit: () => this.#restoreDefault(),
    });
  }

  #restoreDefault() {
    const value = this.dataset.defaultValue ?? 'none';
    for (const input of this.querySelectorAll('input[type="radio"]')) {
      if (input instanceof HTMLInputElement) input.checked = input.value === value;
    }
    getBundleStore().publish('subscription');
  }

  #onModelUpdate = () => {
    getBundleStore().setContext({ productId: this.dataset.productId ?? '', sectionId: this.dataset.sectionId ?? '' });
    this.#restoreDefault();
  };
}

if (!customElements.get('buy-box-v2-plans')) {
  customElements.define('buy-box-v2-plans', BuyBoxV2Plans);
}
